/*
 * Copyright (c) 2024, King Living
 * @override - Use local Field and useRegistrationFields with enhanced validation
 *
 * Validation Rules:
 * - Email: Valid format, character limit
 * - FirstName/LastName: Alpha only, apostrophes allowed, no numbers/emojis/special chars
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import Field from '@amblique/king-living/app/components/field';
import {FieldLabelContent} from '@amblique/king-living/app/components/field/field-label-content';
import PasswordRequirements from '@amblique/king-living/app/components/forms/password-requirements';
import useRegistrationFields from '@amblique/king-living/app/components/forms/useRegistrationFields';
import {
  Box, Input, Stack, Text, useMultiStyleConfig,
} from '@amblique/king-living/app/components/shared/ui';

const RegistrationFields = ({form, prefilledEmail, prefix = ''}) => {
  const fields = useRegistrationFields({form, prefix});
  const password = form.watch('password');
  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <Stack spacing={3}>
      {prefilledEmail ? (
        <Box {...styles.readOnlyEmailContainer}>
          <Text {...styles.readOnlyEmailLabel}>
            <FieldLabelContent
              label={
                <FormattedMessage
                  defaultMessage="Email address"
                  id="use_registration_fields.label.email"
                />
              }
              required
            />
          </Text>
          <Text {...styles.readOnlyEmailValue}>{prefilledEmail}</Text>
          <Input
            type="hidden"
            {...form.register('email')}
            value={prefilledEmail}
          />
        </Box>
      ) : (
        <Field {...fields.email} />
      )}
      <Field {...fields.firstName} />
      <Field {...fields.lastName} />

      <Stack mb={1} spacing={4}>
        <Field
          {...fields.password}
          autoComplete="new-password"
          inputProps={{id: 'password'}}
        />
        <PasswordRequirements value={password} />
      </Stack>

      <Box marginTop={4}>
        <Field {...fields.acceptsMarketing} inputProps={{alignItems: 'flex-start'}} />
      </Box>
    </Stack>
  );
};

RegistrationFields.propTypes = {
  /** Object returned from `useForm` */
  form: PropTypes.object.isRequired,

  /** Prefilled email from login flow — shown read-only per AUTH-06 */
  prefilledEmail: PropTypes.string,

  /** Optional prefix for field names */
  prefix: PropTypes.string,
};

export default RegistrationFields;
